/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { Component, computed, effect, inject, untracked, input } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../auth.service';
import { FullLangString, LanguageService, MinLangString } from '../../../language.service';
import { BaseUserInfoApiService } from './../../../api/base-user-info-api.service';
import { ItemSelectComponent } from '../item-select/item-select.component';
import { MatMiniFabButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

@Component({
  selector: 'vitamui-common-select-language',
  templateUrl: './select-language.component.html',
  styleUrls: ['./select-language.component.scss'],
  imports: [ItemSelectComponent, MatMiniFabButton, MatMenuTrigger, MatMenu, MatMenuItem, TranslatePipe],
})
export class SelectLanguageComponent {
  private readonly translateService = inject(TranslateService);
  private readonly languageService = inject(LanguageService);
  private readonly userInfoApiService = inject(BaseUserInfoApiService);
  private readonly authService = inject(AuthService);

  /**
   * This component have two display mode :
   * select : displays a select box with the current selected lang as text.
   * button : displays a circle button with the current selected lang as an image.
   */
  readonly displayMode = input<'select' | 'button'>('button');

  public minLangString = MinLangString;

  public currentLang = computed(() => (this.translateService.currentLang() as string) ?? '');

  private userInfo = toSignal(this.authService.getUserInfo$(), { initialValue: null });

  constructor() {
    // Sync user preference to TranslateService after render (effect runs outside the current CD cycle).
    // Use untracked() for currentLang so the effect only tracks userInfo, not language changes,
    // otherwise switching to 'en' would re-trigger the effect and revert to the stored 'fr' preference.
    effect(() => {
      const userInfo = this.userInfo();
      if (!userInfo?.language) return;
      const shortLang = this.languageService.getShortLangString(userInfo.language as FullLangString);
      const current = untracked(() => this.translateService.getCurrentLang());
      if (current !== shortLang) {
        this.translateService.use(shortLang);
      }
    });
  }

  public use(lang: MinLangString): void {
    this.userInfoApiService.patchMyUserInfo({ language: this.languageService.getFullLangString(lang) }).subscribe(() => {
      this.translateService.use(lang);
    });
  }
}
